import ZtServiceFactory from '../zt';

const SHARE_POSTER_PRIZE_ID = 'share_poster';
const LEGAL_PRIZE_ENUMS = [0, 1, 2];
const COMPLETE_DECIMAL_PATTERN = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i;
const INVALID_PRIZE_CONTEXT_MESSAGE = 'invalid prize context';
const INVALID_BINDING_MESSAGE = 'invalid backpack binding';
const UNSUPPORTED_PRIZE_MESSAGE = 'unsupported backpack prize';

/**
 * Normalizes the backend's documented reward enums without treating partial numeric text as valid.
 * @param {number|string} value Raw prize_time or claim_status.
 * @returns {0|1|2|null} Supported enum value, otherwise null to keep claiming disabled.
 */
function normalizePrizeEnum(value) {
  let normalizedValue = value;

  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    if (!trimmedValue || !COMPLETE_DECIMAL_PATTERN.test(trimmedValue)) {
      return null;
    }
    normalizedValue = Number(trimmedValue);
  }

  if (typeof normalizedValue !== 'number' || !Number.isFinite(normalizedValue) || LEGAL_PRIZE_ENUMS.indexOf(normalizedValue) === -1) {
    return null;
  }
  return normalizedValue;
}

/**
 * Preserves documented string/number binding fields while rejecting empty and non-finite values.
 * @param {string|number} value Raw bind_role field.
 * @returns {string|null} Stable field or null when the binding cannot be authoritative.
 */
function normalizeBindingField(value) {
  if ((typeof value !== 'string' && typeof value !== 'number') || (typeof value === 'number' && !Number.isFinite(value))) {
    return null;
  }

  const normalizedValue = String(value).trim();
  return normalizedValue || null;
}

/**
 * Restores binding ownership from any non-empty bind_role object.
 * Relaxed 2026-09-23 (user decision): the live get_prize_list response omits role_id/server_id, so the
 * previous four-field gate misread already-bound accounts as unbound and re-opened the binding modal
 * after every refresh. Being bound now only requires the backend to report a binding object; field
 * values are normalized best-effort and may be null. Claiming without a real binding is left to
 * claim_prize backend errors instead of client-side rejection.
 * @param {Object} rawBinding Raw get_prize_list.bind_role value.
 * @returns {Object|null} Stable binding ViewModel, or null when the response carries no binding
 *   object (null / array / primitive / empty object; the documented unbound shape is `[]`).
 */
function normalizeBinding(rawBinding) {
  if (!rawBinding || typeof rawBinding !== 'object' || Array.isArray(rawBinding) || Object.keys(rawBinding).length === 0) {
    return null;
  }

  return {
    roleName: normalizeBindingField(rawBinding.role),
    roleId: normalizeBindingField(rawBinding.role_id),
    serverName: normalizeBindingField(rawBinding.server),
    serverId: normalizeBindingField(rawBinding.server_id)
  };
}

/**
 * Maps get_prize_list data to the stable reward state consumed by BackpackStore.
 * @param {Object} rawContext Raw activity response data.
 * @returns {{prizeTime: number|null, reward: Object|null, binding: Object|null}} Stable prize context.
 */
export function normalizePrizeContext(rawContext) {
  if (!rawContext || typeof rawContext !== 'object' || Array.isArray(rawContext) || !Array.isArray(rawContext.prize_list)) {
    throw new Error(INVALID_PRIZE_CONTEXT_MESSAGE);
  }

  const prizeTime = normalizePrizeEnum(rawContext.prize_time);
  const sharePosterPrize = rawContext.prize_list.find(prize => prize && prize.prize_id === SHARE_POSTER_PRIZE_ID);
  const claimStatus = sharePosterPrize ? normalizePrizeEnum(sharePosterPrize.claim_status) : null;
  let reward = null;

  if (sharePosterPrize) {
    reward = {
      prizeId: SHARE_POSTER_PRIZE_ID,
      claimStatus,
      claimable: prizeTime === 1 && claimStatus === 1
    };
  }

  return {
    prizeTime,
    reward,
    binding: normalizeBinding(rawContext.bind_role)
  };
}

/**
 * Loads the activity prize context through the generated ZT service signature.
 * @param {Object} options Adapter dependencies.
 * @param {Function} options.provider Optional raw provider for unit tests.
 * @returns {Promise<Object>} Stable prize context.
 */
export function loadBackpackRewards(options = {}) {
  const provider = options.provider || (mockPassportAccount => ZtServiceFactory.getDefaultInstance().getPrizeList(mockPassportAccount));

  return Promise.resolve()
    .then(() => provider(undefined))
    .then(normalizePrizeContext);
}

/**
 * Binds the selected role while hiding mutation response metadata from the page.
 * @param {Object} binding Role and server values selected by the binding modal.
 * @param {Object} options Adapter dependencies.
 * @param {Function} options.provider Optional mutation provider for unit tests.
 * @returns {Promise<void>} Resolves after the formal binding request succeeds.
 */
export function bindBackpackRole(binding, options = {}) {
  const normalizedBinding = binding && {
    roleName: normalizeBindingField(binding.roleName),
    roleId: normalizeBindingField(binding.roleId),
    serverName: normalizeBindingField(binding.serverName),
    serverId: normalizeBindingField(binding.serverId)
  };

  if (!normalizedBinding || !normalizedBinding.roleName || !normalizedBinding.roleId || !normalizedBinding.serverName || !normalizedBinding.serverId) {
    return Promise.reject(new Error(INVALID_BINDING_MESSAGE));
  }

  const provider =
    options.provider ||
    ((mockPassportAccount, roleName, roleId, serverName, serverId) =>
      ZtServiceFactory.getDefaultInstance().bindRole(mockPassportAccount, roleName, roleId, serverName, serverId));

  return Promise.resolve()
    .then(() => provider(undefined, normalizedBinding.roleName, normalizedBinding.roleId, normalizedBinding.serverName, normalizedBinding.serverId))
    .then(() => undefined);
}

/**
 * Claims the only activity reward owned by this page and hides mutation response metadata.
 * @param {string} prizeId Prize ID from the normalized reward ViewModel.
 * @param {Object} options Adapter dependencies.
 * @param {Function} options.provider Optional mutation provider for unit tests.
 * @returns {Promise<void>} Resolves after the formal claim request succeeds.
 */
export function claimBackpackPrize(prizeId, options = {}) {
  if (prizeId !== SHARE_POSTER_PRIZE_ID) {
    return Promise.reject(new Error(UNSUPPORTED_PRIZE_MESSAGE));
  }

  const provider =
    options.provider || ((mockPassportAccount, selectedPrizeId) => ZtServiceFactory.getDefaultInstance().claimPrize(mockPassportAccount, selectedPrizeId));

  return Promise.resolve()
    .then(() => provider(undefined, prizeId))
    .then(() => undefined);
}

export default {
  normalizePrizeContext,
  loadBackpackRewards,
  bindBackpackRole,
  claimBackpackPrize
};
